import React, { useState, useMemo, useEffect } from "react";
import { X, Layers, Filter } from "lucide-react";

/**
 * AlpacaGroupSelectionModal Component
 * Dedicated Bulk Modify Selector for Alpaca Orders & Positions.
 * Allows users to choose Strategy, Symbol (e.g. AAPL), and Side to filter Alpaca orders for bulk modification.
 */
export function AlpacaGroupSelectionModal({
  isOpen,
  onClose,
  orders = [],
  onConfirm
}) {
  const [selectedStrategy, setSelectedStrategy] = useState("All");
  const [selectedSymbol, setSelectedSymbol] = useState("All");
  const [selectedSide, setSelectedSide] = useState("All");
  const [error, setError] = useState("");

  // Normalize strategy helper
  const getCleanStrategy = (strat) => {
    const s = String(strat || "").trim().toUpperCase();
    if (!s || s === "NONE" || s === "N/A" || s === "SIMPLE" || s === "DEFAULT") return "NO_STRATEGY";
    return s;
  };

  // Normalize side helper
  const getCleanSide = (side) => {
    const s = String(side || "").trim().toUpperCase();
    if (s === "1" || s === "BUY" || s === "B") return "BUY";
    if (s === "-1" || s === "SELL" || s === "S" || s === "SHORT") return "SELL";
    return s || "BUY";
  };

  // Pre-process & filter Alpaca orders
  const validOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    return orders.filter(o => {
      // Ensure it's a valid active/pending order
      const oStatus = String(o.status || o.orderStatus || "").toLowerCase();
      const isPending = !oStatus || ["pending", "open", "new", "accepted", "partially_filled", "trigger pending", "not sent", "6"].includes(oStatus);
      return isPending;
    });
  }, [orders]);

  // Extract unique Strategies, Symbols, Sides
  const options = useMemo(() => {
    const strategies = new Set();
    const symbols = new Set();
    const sides = new Set();

    validOrders.forEach(o => {
      strategies.add(getCleanStrategy(o.strategy));
      if (o.symbol) symbols.add(String(o.symbol).toUpperCase());
      sides.add(getCleanSide(o.side || o.order_side));
    });

    return {
      strategies: Array.from(strategies).sort(),
      symbols: Array.from(symbols).sort(),
      sides: Array.from(sides).sort()
    };
  }, [validOrders]);

  // Reset dropdown selections when modal opens or available options change
  useEffect(() => {
    if (isOpen) {
      setSelectedStrategy("All");
      setSelectedSymbol("All");
      setSelectedSide("All");
      setError("");
    }
  }, [isOpen]);

  // Filter matching orders based on selected dropdowns
  const matchingOrders = useMemo(() => {
    return validOrders.filter(o => {
      const matchStrat = selectedStrategy === "All" || getCleanStrategy(o.strategy) === selectedStrategy;
      const matchSym = selectedSymbol === "All" || String(o.symbol || "").toUpperCase() === selectedSymbol;
      const matchSide = selectedSide === "All" || getCleanSide(o.side || o.order_side) === selectedSide;
      return matchStrat && matchSym && matchSide;
    });
  }, [validOrders, selectedStrategy, selectedSymbol, selectedSide]);

  const handleContinue = () => {
    if (matchingOrders.length === 0) {
      setError("No orders match the selected criteria.");
      return;
    }
    setError("");
    onConfirm(matchingOrders);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[65] backdrop-blur-sm p-4 text-xs">
      <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700/50 flex justify-between items-center bg-[#14161f]">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
            <h2 className="text-white text-base font-bold">Alpaca Bulk Modify Selector</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-400 text-[11px]">
            Select the group of Alpaca orders you want to modify in bulk across client accounts.
          </p>

          {/* Strategy Dropdown */}
          <div>
            <label className="block text-gray-300 font-semibold mb-1.5 uppercase tracking-wider text-[10px]">Strategy</label>
            <select
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
              className="w-full bg-[#14161f] border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 font-mono"
            >
              <option value="All">All Strategies</option>
              {options.strategies.map((strat, idx) => (
                <option key={idx} value={strat}>{strat}</option>
              ))}
            </select>
          </div>

          {/* Symbol Dropdown */}
          <div>
            <label className="block text-gray-300 font-semibold mb-1.5 uppercase tracking-wider text-[10px]">Symbol</label>
            <select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="w-full bg-[#14161f] border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 font-mono"
            >
              <option value="All">All Symbols</option>
              {options.symbols.map((sym, idx) => (
                <option key={idx} value={sym}>{sym}</option>
              ))}
            </select>
          </div>

          {/* Side Dropdown */}
          <div>
            <label className="block text-gray-300 font-semibold mb-1.5 uppercase tracking-wider text-[10px]">Side</label>
            <select
              value={selectedSide}
              onChange={(e) => setSelectedSide(e.target.value)}
              className="w-full bg-[#14161f] border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 font-mono"
            >
              <option value="All">All Sides</option>
              {options.sides.map((side, idx) => (
                <option key={idx} value={side}>{side}</option>
              ))}
            </select>
          </div>

          {/* Actionable Matches Counter */}
          <div className="bg-[#14161f] border border-gray-800 rounded-lg p-3 flex justify-between items-center">
            <span className="text-gray-400 font-medium">Actionable Matches:</span>
            <span className="bg-blue-600/20 text-blue-400 border border-blue-500/30 px-3 py-1 rounded font-bold font-mono text-sm">
              {matchingOrders.length}
            </span>
          </div>

          {error && (
            <div className="text-red-400 text-[11px] bg-red-950/40 border border-red-900/50 p-2 rounded text-center">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700/50 bg-[#14161f] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={matchingOrders.length === 0}
            className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue to Modify
          </button>
        </div>
      </div>
    </div>
  );
}
